import { GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { EOQType, Feature, Manufacturer, Product, ProductType } from 'app/model/entities/entity-model';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { BulkImageUploadDialogComponent } from './bulk-image-upload-dialog.component';
import { AgFns, GridOptionsExt, GridState, ISortModel } from 'app/shared/ag-fns';
// import { UniAgFns, GridOptionsExt } from 'app/shared/uni-ag-fns'
import { DomainBaseComponent } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import { UniAgFns } from 'app/shared/uni-ag-fns';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-product-types',
  templateUrl: './product-types.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProductTypesComponent extends DomainBaseComponent implements OnInit {
  _isShowingEmptyProductTypes = false;
  gridState: GridState;

  itsGridOptions: GridOptionsExt;
  itsEmptyGridOptions: GridOptionsExt;
  productTypes: ProductType[] = [];
  selectedProductType: ProductType;

  altItsGridOptions: GridOptionsExt;
  altProductTypes: ProductType[] = [];

  issGridOptions: GridOptionsExt;
  products: Product[];

  features: Feature[];
  eOQTypes: EOQType[];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
  ) {
    super(domainService);
    this.user = domainService.authService.getUser();
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  get isShowingEmptyProductTypes() {
    return this._isShowingEmptyProductTypes;
  }
  set isShowingEmptyProductTypes(value: boolean) {
    if (value == null || value == this._isShowingEmptyProductTypes) {
      return;
    }
    this.saveGridState();
    this._isShowingEmptyProductTypes = value;
    this.restoreGridState();
  }

  async updateFromContext() {
    this.itsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItsGridReady,
      onRowSelected: this.onItsRowSelected,
      onFilterChanged: this.onFilterChanged,
      onModelUpdated: this.onModelUpdated,
      rowModelType: 'serverSide',
    });
    this.itsGridOptions.onPaginationChanged = (params) => {
      if (params.newPage) {
        this.selectedProductType = null;
        this.productTypes = [];
        this.altProductTypes = [];
        this.updateLocation();
      }
      AgFns.refreshGrid(this.issGridOptions, []);
      AgFns.refreshGrid(this.altItsGridOptions, []);
    };
    // AgFns.captureGridRouteParams(this.itsGridOptions, this.route, "id");
    this.captureLocation(this.itsGridOptions, 'id');

    this.itsEmptyGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItsGridReady,
      onRowSelected: this.onItsRowSelected,
    });

    this.altItsGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAltItsGridReady,
    });

    this.issGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onIssGridReady,
    });

    this.features = await this.dbQueryService.getFeatures();
    this.eOQTypes = this.dbQueryService.getAllCached(EOQType);
    this.isPageReady = true;
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(Product);
    this.uow.clearEntities(ProductType);
    this.uow.clearEntities(Manufacturer);
    return true;
  }

  async onItsGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps('', this.editProductType.bind(this), {
          label: 'Edit',
        }),
        ...AgFns.fixWidth(100),
      },
      { ...UniAgFns.getManufacturerColDef('manufacturer.name'), ...AgFns.flexWidth(140) },
      { ...UniAgFns.getProductTypeSKUColDef('style'), ...AgFns.flexWidth(140) },
      { ...UniAgFns.getProductTypeDescriptionColDef('description'), ...AgFns.flexWidth(200) },
      {
        headerName: 'Feature Names',
        field: 'featureNamesExtract',
        sortable: false,
        ...AgFns.flexWidth(180),
      },

      { headerName: 'EOQ Type', field: 'eOQType.name', ...AgFns.fixWidth(140) },

      { ...UniAgFns.getActiveStatusColDef('activeStatus.name') },

      // { headerName: 'Id', field: 'id', filter: 'agNumberColumnFilter' },
    ];

    const sortModel = [
      { colId: 'manufacturer.name', sort: 'asc' },
      { colId: 'description', sort: 'asc' },
      { colId: 'style', sort: 'asc' },
    ] as ISortModel;

    if (this.isShowingEmptyProductTypes) {
      AgFns.initGrid(event, colDefs, sortModel);
      await this.refreshEmptyProductTypes();
    } else {
      AgFns.initGrid(event, colDefs, sortModel);
      await this.updateDatasource();
      await this.applyLocation();
    }
    // AgFns.selectDefaultRow(this.itsGridOptions);
  }

  async onAltItsGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...UniAgFns.getManufacturerColDef('manufacturer.name') },
      { ...UniAgFns.getProductTypeSKUColDef('style') },
      { ...UniAgFns.getProductTypeDescriptionColDef('description') },

      // { headerName: "Id", field: "id", filter: "agNumberColumnFilter" },
    ];

    const sortModel = [
      { colId: 'manufacturer.name', sort: 'asc' },
      { colId: 'description', sort: 'asc' },
      { colId: 'style', sort: 'asc' },
    ] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  async onIssGridReady(event: GridReadyEvent) {
    const colDefs = [
      UniAgFns.getProductSortOrderColDef((x) => x.data, 'productSortOrder', false),
      {
        headerName: 'Value',
        field: 'featureChoicesExtract',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
      },

      {
        headerName: 'Reorder Value',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(200),
        valueGetter: (params) => {
          const prod = <Product>params.data;
          return prod && prod.getReorderProduct().getFeatureChoicesExtract();
        },
      },
      { ...UniAgFns.getActiveStatusColDef('activeStatus.name'), ...AgFns.fixWidth(120) },
      // { headerName: "Id", field: "id", filter: "agNumberColumnFilter" },
    ];

    const sortModel = [{ colId: 'productSortOrder', sort: 'asc' }] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  async updateDatasource() {
    const gridApi = this.itsGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createProductTypesQuery());
    gridApi.setGridOption('serverSideDatasource', ds);

    await this.applyLocation();
  }

  onFilterChanged() {
    this.updateLocation();
  }

  async onItsRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }

    const its = e.data as ProductType;
    if (!its) {
      return;
    }
    this.updateLocation(its.id);
    this.selectedProductType = its;

    // next two are both async but we don't need to wait.
    this.getAltProductTypes(its);
    this.getProducts(its);
  }

  saveGridState(key: string = null) {
    const gridInfo = this.getCurrentGridInfo();
    const gridState = AgFns.getAndAttachGridState(gridInfo.gridOptions, key ?? this.selectedProductType?.id.toString(), 'id');
  }

  restoreGridState() {
    const gridInfo = this.getCurrentGridInfo();
    this.gridState = gridInfo.gridOptions?.context?.gridState ?? this.gridState;
  }

  onModelUpdated() {
    this.altProductTypes = [];
    this.products = [];
  }

  updateLocation(key: any = null) {
    // don't both saving location for empty product types because its a different rowModel
    if (this.isShowingEmptyProductTypes) {
      return;
    }
    this.replaceLocation(key);
  }

  async getAltProductTypes(its: ProductType) {
    this.altProductTypes = await AgFns.busyGrid(this.altItsGridOptions, this, () => {
      return this.dbQueryService.getAltProductTypesForProductType(its.id);
    });
    return this.altProductTypes;
  }

  async getProducts(its: ProductType) {
    this.products = await AgFns.busyGrid(this.issGridOptions, this, () => {
      this.products = [];
      return this.dbQueryService.getProductsForProductType(its.id);
    });
    // This can occur async with the rest of the app
    // const featureChoices = await this.dbQueryService.getFeatureChoices(its.featureId);
    return this.products;
  }

  private async refreshEmptyProductTypes() {
    this.products = [];
    this.altProductTypes = [];
    this.productTypes = await this.dbQueryService.getEmptyProductTypes();
  }

  async onTabChanged(evt: any) {
    // HACK: needed because AgGrid loses buttons when tabbing from one tab to another and then back.
    this.products = this.products.slice();
    this.altProductTypes = this.altProductTypes.slice();
  }

  getCurrentGridInfo() {
    return this.isShowingEmptyProductTypes
      ? { name: 'its-empty', gridOptions: this.itsEmptyGridOptions }
      : { name: 'its', gridOptions: this.itsGridOptions };
  }

  addNewProductType() {
    this.router.navigate(['../product-type', 'add'], { relativeTo: this.route });
  }

  editProductType(its: ProductType) {
    this.updateLocation(its.id);
    this.router.navigate(['../product-type', its.id], { relativeTo: this.route });
    return;
  }

  goProductSearch() {
    this.router.navigate(['/product-search']);
  }

  async bulkUploadImages() {
    const result = await DialogBaseComponent.open(BulkImageUploadDialogComponent, this, {
      dialogWidth: '90vw',
      dialogHeight: '85vh',
    });
    if (result) {
      await this.updateDatasource();
    }
  }

  // goProductTypeTags() {
  //   this.router.navigate(["/references/manage/product-type-tags"]);
  // }

  // goFeatures() {
  //   this.router.navigate(["/references/manage/features"]);
  // }
}
